import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { LogOut, ChevronLeft, AlertTriangle } from "lucide-react";
import { KD9LogoHorizontal, KD9Watermark } from "@/components/KD9Logo";
import { useRole, ROLE_META } from "@/context/RoleContext";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { ROLE_MENUS, getAcademicAccessSection, getDelegatedPermissionSection, isMenuItemAllowed, moduleForPath } from "@/lib/roleMenus";

const Sidebar = ({ collapsed, setCollapsed, forceVisible = false }) => {
  const navigate = useNavigate();
  const { role } = useRole();
  const { logout, accessMap } = useAuth();
  const { t } = useLanguage();
  const meta = ROLE_META[role];
  const menu = ROLE_MENUS[role] || ROLE_MENUS.user;

  // Fase 4 · gating por permisos REALES del backend (/api/me/access-map)
  // — reemplaza el mapa estático legacy que causaba mismatch de claves.
  const backendPerms = React.useMemo(
    () => new Set(accessMap?.permissions || []),
    [accessMap]
  );
  const backendFlags = accessMap?.flags || {};
  const delegatedPerms = React.useMemo(
    () => new Set(accessMap?.delegated_permissions || []),
    [accessMap]
  );
  const consultantSetup = accessMap?.consultant_setup || {};
  const menuSections = React.useMemo(() => {
    const delegated = getDelegatedPermissionSection(role, delegatedPerms);
    const academic = getAcademicAccessSection(role, accessMap);
    const baseSections = [
      ...menu.sections.filter((section) => section.sectionKey !== "sidebar.section.cuenta"),
      ...(academic ? [academic] : []),
      ...(delegated ? [delegated] : []),
      ...menu.sections.filter((section) => section.sectionKey === "sidebar.section.cuenta"),
    ];
    if (role === "consultor" && consultantSetup.required) {
      return [
        {
          label: "Activación pendiente",
          priority: true,
          items: [
            {
              to: "/app/perfil?consultantSetup=1",
              label: "Activar mis servicios",
              icon: AlertTriangle,
              badge: "PENDIENTE",
            },
          ],
        },
        ...baseSections,
      ];
    }
    return baseSections;
  }, [consultantSetup.required, delegatedPerms, accessMap, menu.sections, role]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <aside
      className={`relative flex-col overflow-hidden transition-all duration-300 ${
        forceVisible
          ? "flex h-full w-full"
          : "hidden lg:flex h-screen sticky top-0"
      }`}
      style={{
        width: forceVisible ? "100%" : (collapsed ? 88 : 280),
        background: "var(--kd9-bg-2)",
        borderRight: "1px solid var(--kd9-border)",
      }}
      data-testid="dashboard-sidebar"
    >
      <KD9Watermark size={420} className="-bottom-10 -right-20 rotate-12" />

      <div
        className="relative z-10 px-5 py-6 flex items-center justify-between border-b"
        style={{ borderColor: "var(--kd9-border)" }}
      >
        <Link to="/app/inicio">
          {collapsed ? (
            <div className="font-cinzel kd9-gold-text text-2xl font-bold">9</div>
          ) : (
            <KD9LogoHorizontal size={36} />
          )}
        </Link>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-7 h-7 rounded-full flex items-center justify-center border"
          style={{
            borderColor: "var(--kd9-border)",
            background: "var(--kd9-accent-soft)",
          }}
          data-testid="sidebar-collapse-btn"
          aria-label="Colapsar"
        >
          <ChevronLeft
            size={14}
            style={{
              color: "var(--kd9-accent)",
              transition: "transform .3s",
              transform: collapsed ? "rotate(180deg)" : "none",
            }}
          />
        </button>
      </div>

      {/* role badge */}
      {!collapsed && (
        <div
          className="relative z-10 mx-4 mt-4 mb-2 px-3 py-2 rounded-lg border flex items-center gap-2"
          style={{
            borderColor: `${meta.color}55`,
            background: `${meta.color}14`,
          }}
          data-testid="sidebar-role-badge"
        >
          <span
            className="w-6 h-6 rounded-full flex items-center justify-center font-cinzel text-[10px]"
            style={{ background: meta.color, color: "#0a0a0a" }}
          >
            {meta.glyph}
          </span>
          <div
            className="font-mont text-[10px] tracking-[0.22em] uppercase font-semibold"
            style={{ color: meta.color }}
          >
            {meta.label}
          </div>
        </div>
      )}

      <nav className="relative z-10 flex-1 overflow-y-auto px-3 py-3 pb-6 space-y-4">
        {menuSections.map((section, sIdx) => (
          <div key={sIdx}>
            {!collapsed && (
              <div
                className={`px-3 text-[10px] font-mont tracking-[0.3em] uppercase mb-2 flex items-center gap-2 ${
                  section.priority ? "kd9-accent-text" : ""
                }`}
                style={{
                  color: section.priority
                    ? "var(--kd9-accent)"
                    : "var(--kd9-text-soft)",
                }}
              >
                {section.priority && (
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      background: "var(--kd9-accent)",
                      boxShadow: "0 0 8px var(--kd9-accent)",
                    }}
                  />
                )}
                {section.sectionKey ? t(section.sectionKey) : section.label}
              </div>
            )}
            <div className="space-y-0.5">
              {section.items
                .filter((item) => {
                  if (item.flagRequired && !backendFlags[item.flagRequired]) return false;
                  const moduleKey = moduleForPath(item.to);
                  if (role !== "superadmin" && moduleKey && accessMap?.modules?.[moduleKey] === false) return false;
                  return isMenuItemAllowed(item, backendPerms);
                })
                .map((item) => (
                  <SidebarLink key={item.to} item={item} collapsed={collapsed} priority={section.priority} t={t} />
                ))}
            </div>
          </div>
        ))}
        {/* Spacer so support hint + logout don't overlap last items on tall menus */}
        <div className="h-32" aria-hidden="true" />
      </nav>

      <button
        onClick={handleLogout}
        className="relative z-10 mx-4 mb-5 flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-mont transition-all border"
        style={{
          color: "var(--kd9-text-soft)",
          borderColor: "var(--kd9-border)",
        }}
        data-testid="sidebar-logout-btn"
      >
        <LogOut size={16} />
        {!collapsed && <span>{t("sidebar.logout")}</span>}
      </button>
    </aside>
  );
};

const SidebarLink = ({ item, collapsed, priority, t }) => {
  const Icon = item.icon;
  const label = item.labelKey ? t(item.labelKey) : item.label;
  const testidBase = (item.labelKey || item.label || item.to)
    .toLowerCase()
    .replace(/\s/g, "-")
    .replace(/[^a-z0-9-]/g, "");
  return (
    <NavLink
      to={item.to}
      data-testid={`sidebar-link-${testidBase}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2.5 rounded-xl font-mont text-sm transition-all ${
          isActive ? "kd9-glow" : ""
        }`
      }
      style={({ isActive }) => ({
        background: isActive
          ? priority
            ? "var(--kd9-accent-soft)"
            : "rgba(212,175,55,0.10)"
          : "transparent",
        color: isActive
          ? "var(--kd9-accent)"
          : "var(--kd9-text-soft)",
      })}
    >
      <span
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{
          background: priority
            ? "var(--kd9-accent-soft)"
            : "var(--kd9-accent-soft)",
          border: "1px solid var(--kd9-border)",
        }}
      >
        <Icon size={14} />
      </span>
      {!collapsed && (
        <span className="flex-1 truncate tracking-[0.03em]">{label}</span>
      )}
      {!collapsed && item.badge && (
        <span
          className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
          style={{
            background: "var(--kd9-accent)",
            color: "var(--kd9-bg)",
          }}
        >
          {item.badge}
        </span>
      )}
    </NavLink>
  );
};

export default Sidebar;

